/**
 * `TrailingParagraphExtension` — guarantees an EDITABLE doc always ends
 * with a `paragraph` node. Registered from `buildEditorExtensions` for
 * the surfaces that can author a block as the document's last node: the
 * wiki, card descriptions and comments.
 *
 * Why this exists:
 *   Panels, expand-section, video-embed are all `isolating: true` (or
 *   block atoms like `horizontalRule` / `image`). When one of them is
 *   the LAST node in the doc, ProseMirror has no natural cursor
 *   position below it — the only way to put the caret there is via the
 *   gap-cursor (a thin horizontal blink that reads as an unfamiliar
 *   "blinking underline" to users not steeped in PM conventions).
 *
 *   Both UX paths surface the same problem:
 *
 *     1. Keyboard — `ArrowDown` from the last line of a panel body
 *        with no node below → gap cursor flashes.
 *     2. Mouse — click in the whitespace below the last block in the
 *        doc → gap cursor flashes.
 *
 *   The earlier `exitIsolatingBlockDown` command handles (1) by
 *   intercepting ArrowDown on the panel itself. It can't handle (2) —
 *   the click path goes through ProseMirror's mouse-handling, which
 *   doesn't route through our extension's keyboard shortcuts.
 *
 *   This extension is the structural fix: by making sure a `paragraph`
 *   always exists after the last non-paragraph block, BOTH UX paths
 *   land in a real text cursor instead of a gap cursor. Notion and
 *   Confluence both ship a similar invariant.
 *
 * What it does NOT do:
 *   - Doesn't insert paragraphs BETWEEN adjacent isolating blocks.
 *     Two panels stacked back-to-back still have a gap cursor between
 *     them. Deferred — the click-below-the-doc case is the high-impact
 *     one; the between-panels case requires a more invasive plugin
 *     that auto-inserts paragraphs on schema constraints + has a real
 *     risk of surprising the user with phantom paragraphs.
 *   - Doesn't remove existing trailing paragraphs. If the user types,
 *     then deletes their text, the empty paragraph stays. Same shape
 *     as a fresh doc — invisible to the user, no surprise.
 *   - Doesn't run on a read-only editor, so a body stored before this
 *     invariant existed renders at its stored height and only gains the
 *     paragraph once someone actually edits it.
 *
 * Schema invariant after this plugin runs:
 *   `doc.lastChild.type.name === 'paragraph'` is always true. Empty
 *   paragraphs at the end are normal — they're the wiki equivalent
 *   of the trailing newline in a Unix file.
 */

import { Plugin, PluginKey } from '@tiptap/pm/state'
import { Extension } from '@tiptap/react'

const trailingParagraphPluginKey = new PluginKey('wiki-trailing-paragraph')

export const TrailingParagraphExtension = Extension.create({
  name: 'trailingParagraph',

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: trailingParagraphPluginKey,
        appendTransaction(transactions, _oldState, newState) {
          // Read-only renders are left exactly as they were stored. The
          // paragraph exists to receive a caret, and a read-only surface
          // has none — appending there would add a blank line under every
          // rendered description that happens to end in a block.
          if (!editor.isEditable) return null
          // Only act when something actually changed; otherwise this
          // would fire a no-op tr on every selection move.
          const docChanged = transactions.some((tr) => tr.docChanged)
          // Also fire on initial mount (no transactions yet), when the
          // doc loads from a saved state that lacks the trailing
          // paragraph. The `transactions.length === 0 && oldState ===
          // newState` case can't happen here (PM only calls
          // appendTransaction when there ARE transactions), so the
          // `docChanged` check is sufficient.
          if (!docChanged) return null

          const doc = newState.doc
          const paragraphType = newState.schema.nodes.paragraph
          if (!paragraphType) return null

          const lastChild = doc.lastChild
          if (lastChild && lastChild.type.name === 'paragraph') {
            // Already ends with a paragraph (possibly empty). Done.
            return null
          }

          // Append an empty paragraph at the doc's end.
          return newState.tr.insert(doc.content.size, paragraphType.create())
        },
      }),
    ]
  },
})
